import React, { useState, useEffect, useCallback } from "react";
import {
  ChevronRight,
  CheckCircle,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import EmailPreviewModal from "./EmailPreviewModal";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import Toast from "../../common/Toast/Toast";
import { emailccRegex } from "../../configs/App.Regex";
import CfLoader from "../../common/CfLoader/CfLoader";
import useNavigationStore from "../../store/navigationStore";

const NotifyRecipients = () => {
  const {
    credential_template_id: paramCredentialTemplateId,
    issuer_credential_template_id: paramIssuerCredentialTemplateId,
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const issuerCredentialTemplateId =
    paramIssuerCredentialTemplateId ||
    location.state?.issuerCredentialTemplateId ||
    localStorage.getItem("issuerCredentialTemplateId");
  const credentialTemplateId =
    paramCredentialTemplateId ||
    location.state?.credentialTemplateId ||
    localStorage.getItem("credentialTemplateId");

  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState("All Recipients");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [recipientsIssuedCount, setRecipientsIssuedCount] = useState(0);
  const [recipientsNotifiedCount, setRecipientsNotifiedCount] = useState(0);
  const [recipientsPendingCount, setRecipientsPendingCount] = useState(0);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [isMultipleSelect, setIsMultipleSelect] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [currentToEmail, setCurrentToEmail] = useState("");
  const [toEmailError, setToEmailError] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [currentCcEmail, setCurrentCcEmail] = useState("");
  const [ccEmailError, setCcEmailError] = useState("");
  const [credentialId, setCredentialId] = useState("");

  useEffect(() => {
    if (
      !currentProgramme ||
      !currentBatch ||
      !credentialTemplateId ||
      !issuerCredentialTemplateId
    ) {
      navigate("/issuer/workspace", { replace: true });
    }
  }, [
    currentProgramme,
    currentBatch,
    credentialTemplateId,
    issuerCredentialTemplateId,
    navigate,
  ]);

  const fetchRecipients = async (page, search = "") => {
    if (!issuerCredentialTemplateId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const offset = (page - 1) * itemsPerPage;
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const notificationStatus =
        activeTab === "Notified"
          ? "&notification_status=sent"
          : activeTab === "Not Notified"
          ? "&notification_status=not_sent"
          : "&order_by_notification_status=not_sent";
      const statusFilter =
        "&status=Issued,Printed,Notified,Printed_and_Notified";

      const response = await GET(
        `${EntityConstants.GET_RECIPIENTS}/${issuerCredentialTemplateId}?limit=${itemsPerPage}&offset=${offset}${searchParam}${notificationStatus}${statusFilter}`
      );

      const data = await response.json();

      const transformedRecipients = data.results.map((r) => {
        let displayStatus;
        switch (r.status) {
          case "Issued":
            displayStatus = "Pending";
            break;
          case "Printed":
            displayStatus = "Pending";
            break;
          case "Notified":
            displayStatus = "Notified";
            break;
          case "Revoked":
            displayStatus = "Pending";
            break;
          case "Failed":
            displayStatus = "Pending";
            break;
          case "Printed_and_Notified":
            displayStatus = "Notified";
            break;
          default:
            displayStatus = "Pending";
        }

        return {
          id: r.credential_id,
          name: r.recipient_name,
          email: r.recipient_email,
          rollNumber: r.roll_number,
          phone: r.recipient_phone,
          issuedOn: r.issued_on,
          notifiedAt: r.notified_at,
          status: displayStatus,
          previewUrl: r.credential_digital_preview,
        };
      });

      setRecipients(transformedRecipients);
      setTotalCount(data.count);
      setCredentialId(data.credential_id);
    } catch (error) {
      console.error("Error fetching recipients:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch recipients",
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefreshTable = async () => {
    setIsRefreshing(true);
    await fetchRecipients(currentPage, searchQuery);
    await fetchRecipientStats();
  };

  const fetchRecipientStats = async () => {
    if (!issuerCredentialTemplateId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await GET(
        `${EntityConstants.GET_RECIPIENT_STATS}/${issuerCredentialTemplateId}`
      );
      const data = await response.json();
      if (data.error) {
        Toast({
          type: "error",
          title: "Error",
          message: data.error,
        });
      } else {
        setRecipientsIssuedCount(data.data.total_recipient);
        setRecipientsNotifiedCount(data.data.notified_recipient);
        setRecipientsPendingCount(data.data.pending_notifications);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch transactions",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchRecipients(1, searchQuery);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, issuerCredentialTemplateId]);

  // Fetch recipients when tab changes
  useEffect(() => {
    fetchRecipients(1, searchQuery);
    setCurrentPage(1);
  }, [activeTab, issuerCredentialTemplateId]);

  useEffect(() => {
    if (!searchQuery) {
      fetchRecipients(currentPage);
    }
  }, [currentPage, itemsPerPage, issuerCredentialTemplateId, searchQuery]);

  useEffect(() => {
    fetchRecipientStats();
  }, [issuerCredentialTemplateId]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleSelectAll = useCallback(
    (selected) => {
      if (selected) {
        setSelectedIds(recipients.map((r) => r.id));
      } else {
        setSelectedIds([]);
      }
    },
    [recipients]
  );

  const handleSelectOne = useCallback((id, selected) => {
    if (selected) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((prevId) => prevId !== id));
    }
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    setSelectedIds([]);
  }, []);

  const handleNotifySelected = useCallback(() => {
    if (selectedIds.length === 0) return;

    const selectedRecipientsData = recipients.filter((r) =>
      selectedIds.includes(r.id)
    );
    setSelectedRecipients(selectedRecipientsData);
    const initialToEmails = [
      ...new Set(selectedRecipientsData.map((r) => r.email)),
    ];
    setToEmails(initialToEmails);
    setIsMultipleSelect(true);
    setIsModalOpen(true);
  }, [selectedIds, recipients]);

  const handleRowClick = useCallback((recipient) => {
    setSelectedRecipient(recipient);
    setIsMultipleSelect(false);
    setToEmails([...new Set([recipient.email])]);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRecipients([]);
    setSelectedRecipient(null);
    setIsMultipleSelect(false);
    setSelectedIds([]);
    setToEmails([]);
    setCurrentToEmail("");
    setToEmailError("");
    setCcEmails([]);
    setCurrentCcEmail("");
    setCcEmailError("");
    handleRefreshTable();
  }, []);

  const handleToEmailChange = (e) => {
    const email = e.target.value;
    setCurrentToEmail(email);
    if (!email || emailccRegex.test(email)) {
      setToEmailError("");
    }
  };

  const handleAddToEmail = () => {
    if (emailccRegex.test(currentToEmail)) {
      if (toEmails.includes(currentToEmail)) {
        setToEmailError("* Email already exists");
      } else {
        setToEmails([...toEmails, currentToEmail]);
        setCurrentToEmail("");
        setToEmailError("");
      }
    } else if (currentToEmail) {
      setToEmailError("* Please enter a valid email");
    }
  };

  const handleRemoveToEmail = (email) => {
    setToEmails(toEmails.filter((item) => item !== email));
    setToEmailError("");
  };

  const handlePasteToEmail = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailMatches =
      pastedText.match(new RegExp(emailccRegex.source, "g")) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (toEmails.includes(email)) {
        setToEmailError(`* Email "${email}" already exists`);
      } else {
        setToEmails((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email)
      );

    if (invalidEmails.length > 0) {
      setToEmailError(
        `The following emails are invalid: ${invalidEmails.join(", ")}`
      );
    } else {
      setToEmailError("");
    }

    setCurrentToEmail("");
  };

  // Similar handlers for CC emails
  const handleCcEmailChange = (e) => {
    const email = e.target.value;
    setCurrentCcEmail(email);
    if (!email || emailccRegex.test(email)) {
      setCcEmailError("");
    }
  };

  const handleAddCcEmail = () => {
    if (emailccRegex.test(currentCcEmail)) {
      if (ccEmails.includes(currentCcEmail)) {
        setCcEmailError("* Email already exists");
      } else {
        setCcEmails([...ccEmails, currentCcEmail]);
        setCurrentCcEmail("");
        setCcEmailError("");
      }
    } else if (currentCcEmail) {
      setCcEmailError("* Please enter a valid email");
    }
  };

  const handleRemoveCcEmail = (email) => {
    setCcEmails(ccEmails.filter((item) => item !== email));
    setCcEmailError("");
  };

  const handlePasteCcEmail = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailMatches = pastedText.match(emailccRegex) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (ccEmails.includes(email)) {
        setCcEmailError(`* Email "${email}" already exists`);
      } else {
        setCcEmails((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email)
      );

    if (invalidEmails.length > 0) {
      setCcEmailError(
        `The following emails are invalid: ${invalidEmails.join(", ")}`
      );
    } else {
      setCcEmailError("");
    }

    setCurrentCcEmail("");
  };

  const formatHeaderText = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toUpperCase();
  };

  const fixedHeaders = [
    { key: "name", display: "NAME" },
    { key: "email", display: "EMAIL" },
    { key: "phone", display: "PHONE" },
    { key: "issuedOn", display: "ISSUED ON" },
    { key: "notifiedAt", display: "NOTIFY AT" },
    { key: "status", display: "STATUS" },
  ];

  const handleSendEmail = useCallback(
    async (subject, body) => {
      setIsLoading(true);
      try {
        const recipientIds = isMultipleSelect
          ? selectedIds
          : [selectedRecipient.id];

        const payload = {
          transaction_log_id: issuerCredentialTemplateId,
          credential_ids: recipientIds,
          to_emails: toEmails,
          cc_emails: ccEmails,
          subject: subject,
          body: body,
        };

        const response = await POST(
          EntityConstants.POST_SEND_NOTIFICATIONS,
          payload
        );
        const data = await response.json();

        if (response.ok) {
          Toast({
            type: "success",
            title: "Success",
            message: data.message || "Notifications sent successfully!",
          });
          handleCloseModal();
        } else {
          throw new Error(data.message || "Failed to send notifications.");
        }
      } catch (error) {
        console.error("Error sending notifications:", error);
        Toast({
          type: "error",
          title: "Error",
          message: error.message || "An unexpected error occurred.",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      issuerCredentialTemplateId,
      selectedIds,
      selectedRecipient,
      toEmails,
      ccEmails,
      isMultipleSelect,
      handleCloseModal,
    ]
  );

  const tabOptions = ["All Recipients", "Notified", "Not Notified"];

  const handleKeyDown = (event, action) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  return (
    <div className="w-full mt-20 bg-cfSecondary min-h-screen">
      {/* Breadcrumbs */}
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-center h-[4rem] sm:items-center justify-between w-full`}
        >
          <div className="">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-center h-[4rem] w-full">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span
                    onClick={() => {
                      console.log("Navigating to: /issuer/workspace");
                      navigate(`/issuer/workspace`);
                    }}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => navigate(`/issuer/workspace`))
                    }
                    tabIndex={0}
                    role="button"
                    aria-label="Navigate to programme"
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    {currentProgramme.programme_name || "Programme"}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span
                    onClick={() => {
                      const navPath = currentProgramme?.programme_code
                        ? `/issuer/workspace/batches/${currentProgramme.programme_code}`
                        : "/issuer/workspace";
                      navigate(navPath);
                    }}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => {
                        const navPath = currentProgramme?.programme_code
                          ? `/issuer/workspace/batches/${currentProgramme.programme_code}`
                          : "/issuer/workspace";
                        navigate(navPath);
                      })
                    }
                    tabIndex={0}
                    role="button"
                    aria-label="Navigate to batch"
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    {currentBatch.batch_name || "Batch"}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span
                    onClick={() => {
                      const navPath =
                        currentProgramme?.programme_code &&
                        currentBatch?.batch_code
                          ? `/issuer/workspace/batches/credentials?programmeId=${currentProgramme.programme_code}&batchId=${currentBatch.batch_code}`
                          : `/issuer/workspace/batches/credentials`;
                      navigate(navPath);
                    }}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => {
                        const navPath =
                          currentProgramme?.programme_code &&
                          currentBatch?.batch_code
                            ? `/issuer/workspace/batches/credentials?programmeId=${currentProgramme.programme_code}&batchId=${currentBatch.batch_code}`
                            : `/issuer/workspace/batches/credentials`;
                        navigate(navPath);
                      })
                    }
                    tabIndex={0}
                    role="button"
                    aria-label="Navigate to templates"
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    Templates
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span
                    className="hover:text-gray-800 cursor-pointer"
                    onClick={() => {
                      const navPath = `/issuer/workspace/batches/summary/${credentialTemplateId}/${issuerCredentialTemplateId}?templateType=Legacy`;
                      navigate(navPath);
                    }}
                    onKeyDown={(e) =>
                      handleKeyDown(e, () => {
                        const navPath = `/issuer/workspace/batches/summary/${credentialTemplateId}/${issuerCredentialTemplateId}?templateType=Legacy`;
                        navigate(navPath);
                      })
                    }
                    tabIndex={0}
                    role="button"
                    aria-label="Navigate to summary"
                  >
                    Summary
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-800 font-medium">
                    Notify Recipients
                  </span>
                </div>
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 mt-8 my-4 px-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => {
                const navPath = `/issuer/workspace/batches/summary/${credentialTemplateId}/${issuerCredentialTemplateId}?templateType=Legacy`;
                navigate(navPath);
              }}
              onKeyDown={(e) =>
                handleKeyDown(e, () => {
                  const navPath = `/issuer/workspace/batches/summary/${credentialTemplateId}/${issuerCredentialTemplateId}?templateType=Legacy`;
                  navigate(navPath);
                })
              }
              aria-label="Go back to summary"
              className="cursor-pointer focus:outline-none"
            >
              <img src={ArrowLeftIcon} alt="Back" className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              Notify Recipients
            </h1>
          </div>
          <p className="text-gray-600">
            Send notifications to certificate recipients
          </p>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 my-4 px-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200 flex flex-col">
          <span className="text-gray-500 text-sm mb-1">Total Recipients</span>
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold">{recipientsIssuedCount}</span>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200 flex flex-col">
          <span className="text-gray-500 text-sm mb-1">Notified</span>
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold">
              {recipientsNotifiedCount}
            </span>
            <CheckCircle className="h-6 w-6 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200 flex flex-col">
          <span className="text-gray-500 text-sm mb-1">
            Pending Notification
          </span>
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold">{recipientsPendingCount}</span>
            <AlertCircle className="h-6 w-6 text-amber-400" />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-4 my-4 px-4">
        <nav className="flex -mb-px">
          {tabOptions.map((tab) => (
            <button
              key={tab}
              className={`py-2 px-4 text-sm font-medium focus:outline-none ${
                activeTab === tab
                  ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                  : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
              } transition-colors duration-200`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Action Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 my-4 px-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search recipients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 py-2 pr-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefreshTable}
            disabled={isLoading || isRefreshing}
            className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleNotifySelected}
            disabled={!selectedIds.length}
            className={`flex items-center gap-1 px-3 py-2 rounded text-sm font-medium text-white ${
              selectedIds.length
                ? "bg-cfButton hover:bg-cfPrimaryDark"
                : "bg-[#8d8fd6] cursor-not-allowed"
            } transition-colors duration-200`}
          >
            <span>Notify Selected</span>
          </button>
        </div>
      </div>

      {/* Recipients Table */}
      <div className="m-4 overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full table-auto min-w-[74.5rem]">
          <thead className="bg-[#e0eaff] h-[3.563rem] text-xs font-medium">
            <tr className="text-center">
              <th className="px-2 py-3">
                <input
                  type="checkbox"
                  checked={
                    recipients.length > 0 &&
                    selectedIds.length === recipients.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              {/* Show fixed headers when loading or no recipients, otherwise dynamic headers */}
              {isLoading || recipients.length === 0 ? (
                <>
                  {fixedHeaders.map((header) => (
                    <th key={header.key} className="px-4 py-3">
                      {header.display}
                    </th>
                  ))}
                </>
              ) : (
                Object.keys(recipients[0])
                  .filter((key) => key !== "id" && key !== "previewUrl") //! Exclude these fields in table header(future reference)
                  .map((key) => (
                    <th key={key} className="px-4 py-3">
                      {formatHeaderText(key)}
                    </th>
                  ))
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td
                  colSpan={fixedHeaders.length + 1}
                  className="py-20 text-center"
                >
                  <div className="flex justify-center items-center">
                    <CfLoader />
                  </div>
                </td>
              </tr>
            ) : recipients.length === 0 ? (
              <tr className="text-center">
                <td
                  colSpan={fixedHeaders.length + 1}
                  className="text-center py-4"
                >
                  No recipients found
                </td>
              </tr>
            ) : (
              recipients.map((recipient) => (
                <tr
                  key={recipient.id}
                  className="text-center hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                  onClick={() => handleRowClick(recipient)}
                  onKeyDown={(e) =>
                    handleKeyDown(e, () => handleRowClick(recipient))
                  }
                  tabIndex={0}
                  role="button"
                  aria-label={`View details for ${recipient.name}`}
                >
                  <td
                    className="px-2 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(recipient.id)}
                      onChange={(e) =>
                        handleSelectOne(recipient.id, e.target.checked)
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  {Object.entries(recipient)
                    .filter(([key]) => key !== "id" && key !== "previewUrl") //! Exclude these fields in table body(future reference)
                    .map(([key, value]) => {
                      if (key === "status") {
                        return (
                          <td key={key} className="px-4 py-3">
                            {value === "Notified" ? (
                              <div className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-4 w-4" />
                                <span>Notified</span>
                              </div>
                            ) : (
                              <div className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-md text-sm font-medium bg-amber-100 text-amber-800">
                                <AlertCircle className="h-4 w-4" />
                                <span>Pending</span>
                              </div>
                            )}
                          </td>
                        );
                      }

                      return (
                        <td key={key} className="px-4 py-3 text-gray-600">
                          {value || "N/A"}
                        </td>
                      );
                    })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between pb-6 px-4 w-full">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>

            <span>previous</span>
          </span>
        </button>

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
        >
          <span className="flex items-center justify-center gap-2">
            <span>Next</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      </div>

      {/* Email Preview Modal */}
      {isModalOpen && (
        <EmailPreviewModal
          recipient={isMultipleSelect ? null : selectedRecipient}
          recipients={isMultipleSelect ? selectedRecipients : []}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          isMultipleSelect={isMultipleSelect}
          toEmails={toEmails}
          currentToEmail={currentToEmail}
          toEmailError={toEmailError}
          onToEmailChange={handleToEmailChange}
          onAddToEmail={handleAddToEmail}
          onRemoveToEmail={handleRemoveToEmail}
          onPasteToEmail={handlePasteToEmail}
          ccEmails={ccEmails}
          setCcEmails={setCcEmails}
          currentCcEmail={currentCcEmail}
          ccEmailError={ccEmailError}
          onCcEmailChange={handleCcEmailChange}
          onAddCcEmail={handleAddCcEmail}
          onRemoveCcEmail={handleRemoveCcEmail}
          onPasteCcEmail={handlePasteCcEmail}
          onSendEmail={handleSendEmail}
          credentialId={credentialId}
        />
      )}
    </div>
  );
};

export default NotifyRecipients;
